import React from 'react'
import { motion } from 'motion/react'
import { LlmQuotaError } from '../api/client'

interface Props {
    quotaError: LlmQuotaError
    onDismiss?: () => void
}

const headline = (q: LlmQuotaError): string => {
    const used = q.used.toLocaleString()
    const limit = q.limit.toLocaleString()
    if (q.scope === 'user_day') {
        return `Daily AI quota reached: ${used} / ${limit} tokens used.`
    }
    if (q.scope === 'user_minute') {
        return `Per-minute AI quota reached: ${used} / ${limit} tokens used.`
    }
    return `AI provider rate limit hit: ${used} / ${limit} tokens in the last ${q.window_seconds}s.`
}

const detail = (q: LlmQuotaError): string => {
    if (q.scope === 'user_day') {
        return 'New checkins won’t be auto-analyzed until your rolling 24h window clears. Manual edits still work.'
    }
    if (q.scope === 'user_minute') {
        return 'Wait about a minute and try again.'
    }
    return 'The shared provider cap is briefly saturated. Try again in a minute.'
}

export const QuotaBanner: React.FC<Props> = ({ quotaError, onDismiss }) => {
    const isDay = quotaError.scope === 'user_day'
    return (
        <motion.div
            className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 360, damping: 28 }}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                    <p className="text-amber-900 font-medium">{headline(quotaError)}</p>
                    <p className="text-amber-800 text-sm mt-1">{detail(quotaError)}</p>
                    {isDay && (
                        <motion.button
                            type="button"
                            onClick={() => console.log('[upsell] daily quota CTA clicked')}
                            whileTap={{ scale: 0.96 }}
                            className="mt-3 inline-flex items-center px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded"
                        >
                            Upgrade for more AI usage
                        </motion.button>
                    )}
                </div>
                {onDismiss && (
                    <motion.button
                        onClick={onDismiss}
                        whileTap={{ scale: 0.9 }}
                        className="text-amber-700 hover:text-amber-900 shrink-0"
                        aria-label="Dismiss"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </motion.button>
                )}
            </div>
        </motion.div>
    )
}
